import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageShell from '../components/ui/PageShell';
import Button from '../components/ui/Button';
import { Globals } from '../globalfunctions.js';
import { apiClient } from '../utils/apiClient';
import { bankHolidays } from '../utils/bankHolidays';
import '../styles/ticketTheme.css';
import './TicketHours.css';

const DAYS = [
  { n: 1, label: 'Mon' }, { n: 2, label: 'Tue' }, { n: 3, label: 'Wed' }, { n: 4, label: 'Thu' },
  { n: 5, label: 'Fri' }, { n: 6, label: 'Sat' }, { n: 7, label: 'Sun' }
];
const PRIORITIES = ['urgent', 'high', 'medium', 'low'];

const formatHoliday = (value) => new Date(`${value}T12:00:00Z`)
  .toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

function TicketHours() {
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [newHoliday, setNewHoliday] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await apiClient.get('/api/slaSettings');
      setForm({
        targets: data.targets,
        enabled: data.enabled, timezone: data.timezone, work_days: data.work_days,
        day_start: data.day_start, day_end: data.day_end, holidays: data.holidays
      });
    } catch (err) {
      setError(err.message || 'Could not load the hours.');
    }
  }, []);

  useEffect(() => {
    if (Globals.LoginTime === null) { navigate('/login'); return; }
    load();
  }, [navigate, load]);

  const change = (patch) => { setSaved(''); setForm((f) => ({ ...f, ...patch })); };
  const changeTarget = (group, priority, value) => change({
    targets: { ...form.targets, [group]: { ...form.targets[group], [priority]: value === '' ? '' : Number(value) } }
  });
  const toggleDay = (n) => change({
    work_days: form.work_days.includes(n) ? form.work_days.filter((d) => d !== n) : [...form.work_days, n].sort()
  });
  const addHoliday = (value) => {
    if (!value || form.holidays.includes(value)) return;
    change({ holidays: [...form.holidays, value].sort() });
  };
  const addYear = () => {
    const year = new Date().getFullYear();
    const all = new Set(form.holidays);
    [year, year + 1].forEach((y) => bankHolidays(y).forEach((d) => all.add(d)));
    change({ holidays: [...all].sort() });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await apiClient.put('/api/slaSettings', form);
      setSaved('Saved. New tickets and the stats use these hours straight away.');
    } catch (err) {
      setError(err.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageShell contentClassName="tk">
      <div className="hrs-page">
        <h1>SLA &amp; Working Hours</h1>
        <p className="hrs-intro">
          Count response and resolution targets in working hours instead of round-the-clock. A ticket that arrives at
          6pm on Friday then starts its clock on Monday morning, rather than breaching over the weekend.
        </p>

        {error && <p className="hrs-error" role="alert">{error}</p>}
        {!form ? (!error && <p aria-live="polite">Loading&hellip;</p>) : (
          <form onSubmit={save} className="hrs-form">
            <label className="hrs-switch">
              <input type="checkbox" checked={form.enabled} onChange={(e) => change({ enabled: e.target.checked })} />
              <span>
                <strong>Use working hours for SLAs</strong>
                <small>{form.enabled ? 'On: targets only count time inside the hours below.' : 'Off: targets count every hour of the day (the original behaviour).'}</small>
              </span>
            </label>

            <fieldset className="hrs-card" disabled={!form.enabled}>
              <legend>Working week</legend>
              <div className="hrs-days" role="group" aria-label="Working days">
                {DAYS.map((d) => (
                  <button
                    key={d.n}
                    type="button"
                    className={`hrs-day${form.work_days.includes(d.n) ? ' is-on' : ''}`}
                    aria-pressed={form.work_days.includes(d.n)}
                    onClick={() => toggleDay(d.n)}
                  >{d.label}</button>
                ))}
              </div>
              <div className="hrs-row">
                <label>Opens<input type="time" value={form.day_start} onChange={(e) => change({ day_start: e.target.value })} required /></label>
                <label>Closes<input type="time" value={form.day_end} onChange={(e) => change({ day_end: e.target.value })} required /></label>
                <label className="hrs-grow">Timezone
                  <input type="text" value={form.timezone} onChange={(e) => change({ timezone: e.target.value })} placeholder="Europe/London" required />
                </label>
              </div>
            </fieldset>

            <fieldset className="hrs-card" disabled={!form.enabled}>
              <legend>Holidays (no clock runs)</legend>
              <div className="hrs-row">
                <label>Add a date<input type="date" value={newHoliday} onChange={(e) => setNewHoliday(e.target.value)} /></label>
                <Button type="button" variant="secondary" onClick={() => { addHoliday(newHoliday); setNewHoliday(''); }} disabled={!newHoliday}>Add</Button>
                <Button type="button" variant="secondary" onClick={addYear}>Add UK bank holidays ({new Date().getFullYear()}&ndash;{new Date().getFullYear() + 1})</Button>
              </div>
              {form.holidays.length === 0 ? <p className="hrs-muted">No holidays added.</p> : (
                <ul className="hrs-holidays">
                  {form.holidays.map((h) => (
                    <li key={h}>
                      {formatHoliday(h)}
                      <button type="button" aria-label={`Remove ${formatHoliday(h)}`} onClick={() => change({ holidays: form.holidays.filter((x) => x !== h) })}>&times;</button>
                    </li>
                  ))}
                </ul>
              )}
            </fieldset>

            <fieldset className="hrs-card">
              <legend>Targets by priority</legend>
              <div className="hrs-targets">
                <div className="hrs-targets-head" aria-hidden="true"><span>Priority</span><span>First reply within (hours)</span><span>Resolve within (hours)</span></div>
                {PRIORITIES.map((p) => (
                  <div key={p} className="hrs-targets-row">
                    <strong>{p[0].toUpperCase() + p.slice(1)}</strong>
                    <label>
                      <span className="hrs-targets-label">First reply (hours)</span>
                      <input type="number" min="0.25" max="720" step="0.25" required value={form.targets.firstResponseHours[p]}
                        aria-label={`${p} first reply hours`} onChange={(e) => changeTarget('firstResponseHours', p, e.target.value)} />
                    </label>
                    <label>
                      <span className="hrs-targets-label">Resolve (hours)</span>
                      <input type="number" min="0.25" max="720" step="0.25" required value={form.targets.resolutionHours[p]}
                        aria-label={`${p} resolution hours`} onChange={(e) => changeTarget('resolutionHours', p, e.target.value)} />
                    </label>
                  </div>
                ))}
              </div>
              <p className="hrs-muted">{form.enabled ? 'These count working hours.' : 'These count every hour of the day until working hours are switched on.'} Changes apply to new tickets and to the stats straight away; existing due dates are not changed.</p>
            </fieldset>

            <div className="hrs-actions">
              <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
              {saved && <span className="hrs-saved" role="status">{saved}</span>}
            </div>
          </form>
        )}
      </div>
    </PageShell>
  );
}

export default TicketHours;
